import { AuthTokenResponseSchema, IDEMPOTENCY_KEY_HEADER, ORG_ID_HEADER } from '@clinic/contracts';
import type { z } from 'zod';
import { sessionStore } from '../auth/session-store';
import { maskPath, record } from '../diag/diag.svelte';
import { reportClientError } from '../diag/report';
import { ApiError, NetworkError, toApiError } from './errors';

/**
 * A read that gets no answer in this long is given up on, as a network failure the query retries,
 * so a stalled mobile connection never leaves a page loading for ever (employer report 2026-10-20).
 * Writes have no deadline: one that may already have been done is never silently abandoned.
 */
export const GET_TIMEOUT_MS = 15_000;

export interface RequestOptions<S extends z.ZodType | undefined = undefined> {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Active organization; sent as X-Org-Id. The server verifies membership itself. */
  orgId?: string;
  /** Validates the response body against the shared contract. */
  schema?: S;
  signal?: AbortSignal;
  /** Sent as Idempotency-Key: one per user action, reused when the same action is retried. */
  idempotencyKey?: string;
  /** Milliseconds until the call is given up on (0 = never). Default: GET_TIMEOUT_MS for reads, none for writes. */
  timeoutMs?: number;
}

/** The caller's signal plus a deadline; `expired()` tells the deadline from the caller leaving. */
function deadline(signal: AbortSignal | undefined, ms: number) {
  const controller = new AbortController();
  let expired = false;
  const onAbort = () => controller.abort(signal?.reason);
  if (signal?.aborted) onAbort();
  else signal?.addEventListener('abort', onAbort, { once: true });
  const timer = ms > 0 ? setTimeout(() => {
    expired = true;
    controller.abort();
  }, ms) : undefined;
  return {
    signal: controller.signal,
    expired: () => expired,
    done: () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    },
  };
}

async function send(
  path: string,
  options: RequestOptions<z.ZodType | undefined>,
  signal: AbortSignal | undefined = options.signal,
): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  const token = sessionStore.get()?.accessToken;
  if (token) headers.authorization = `Bearer ${token}`;
  if (options.orgId) headers[ORG_ID_HEADER] = options.orgId;
  if (options.idempotencyKey) headers[IDEMPOTENCY_KEY_HEADER] = options.idempotencyKey;
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  try {
    return await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      credentials: 'same-origin',
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError();
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * No deadline here on purpose: the server rotates the refresh token as it answers, so abandoning
 * a refresh that may have been done, and asking again with the old cookie, would be read as a
 * stolen token and end the session. A second refresh never starts while one is in flight.
 */
async function doRefresh(): Promise<boolean> {
  const started = performance.now();
  record('refresh', 'start');
  let res: Response;
  try {
    res = await fetch('/v1/auth/refresh', { method: 'POST', credentials: 'same-origin' });
  } catch {
    record('refresh', `no answer after ${Math.round(performance.now() - started)}ms`);
    throw new NetworkError();
  }
  record('refresh', `${res.status} after ${Math.round(performance.now() - started)}ms`);
  // A server or gateway failure says nothing about the session: keep it and let the caller retry.
  // Only an answer from the API itself (4xx) means the refresh token is no longer good.
  if (res.status >= 500) throw new NetworkError();
  if (!res.ok) {
    sessionStore.set(null);
    return false;
  }
  const body = AuthTokenResponseSchema.parse(await res.json());
  sessionStore.set({ accessToken: body.accessToken, user: body.user });
  return true;
}

/**
 * Restores or renews the session from the refresh cookie. Concurrent callers share one request,
 * and tabs are serialized with the Web Locks API so a rotated token is never replayed (which the
 * server would treat as token theft and revoke the session).
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      return locks ? await locks.request('clinic-auth-refresh', doRefresh) : await doRefresh();
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

type Output<S> = S extends z.ZodType ? z.output<S> : unknown;

export async function apiRequest<S extends z.ZodType | undefined = undefined>(
  path: string,
  options: RequestOptions<S> = {},
): Promise<Output<S>> {
  const method = options.method ?? 'GET';
  const limit = deadline(options.signal, options.timeoutMs ?? (method === 'GET' ? GET_TIMEOUT_MS : 0));
  const started = performance.now();
  try {
    let res = await send(path, options, limit.signal);
    if (res.status === 401 && sessionStore.get()) {
      const problem = await toApiError(res.clone());
      if (problem.code === 'AUTH_TOKEN_INVALID' && (await refreshSession())) {
        res = await send(path, options, limit.signal);
      }
    }
    if (!res.ok) {
      const error = await toApiError(res);
      if (error.status === 401) sessionStore.set(null);
      throw error;
    }
    if (res.status === 204) return undefined as Output<S>;
    const json: unknown = await res.json();
    if (!options.schema) return json as Output<S>;
    const parsed = options.schema.safeParse(json);
    if (!parsed.success) {
      // The server and this page disagree on the contract: a bug, whichever side it is on. Field
      // paths and issue codes only, never the values.
      const issues = parsed.error.issues.slice(0, 10).map((i) => `${i.path.join('.') || '(root)'}:${i.code}`);
      reportClientError(
        'contract',
        new Error(`${method} ${maskPath(path.split('?')[0] ?? path)} ${issues.join(', ')}`),
        { traceId: res.headers.get('x-request-id') },
      );
      throw parsed.error;
    }
    return parsed.data as Output<S>;
  } catch (error) {
    if (!limit.expired()) throw error;
    record('timeout', `${method} ${maskPath(path.split('?')[0] ?? path)} after ${Math.round(performance.now() - started)}ms`);
    throw new NetworkError();
  } finally {
    limit.done();
  }
}

/**
 * A file from the API (the clinic export), saved by the browser. Same authentication, refresh
 * and error handling as apiRequest; the file never goes through a public URL.
 */
export async function apiDownload(
  path: string,
  options: { orgId: string; fallbackName: string },
): Promise<void> {
  let res = await send(path, options);
  if (res.status === 401 && sessionStore.get()) {
    const problem = await toApiError(res.clone());
    if (problem.code === 'AUTH_TOKEN_INVALID' && (await refreshSession())) res = await send(path, options);
  }
  if (!res.ok) {
    const error = await toApiError(res);
    if (error.status === 401) sessionStore.set(null);
    throw error;
  }
  const name =
    /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? options.fallbackName;
  saveBlob(await res.blob(), name);
}

/** Hands a file the page made (or downloaded) to the browser's save. */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export { ApiError };
